import React, {
  createContext,
  useState,
  useContext,
  useCallback,
  useEffect,
  useRef,
  useMemo,
} from 'react';
import ConfirmDialog from '../components/ConfirmDialog';

const DialogContext = createContext();

/**
 * DialogProvider — Confirm Dialog를 전역으로 관리하는 Provider
 *
 * Provider 내부에 ConfirmDialog 렌더링 영역을 포함하여,
 * 별도의 Dialog 컨테이너를 Routes 외부에 배치할 필요 없음.
 *
 * Promise 기반 비동기 처리:
 *   const { confirm } = useDialog();
 *   const result = await confirm({
 *     title: '거래 삭제',
 *     message: '정말 삭제하시겠습니까?',
 *     type: 'error',
 *     confirmText: '삭제',
 *   });
 *   if (result) { // 삭제 실행 }
 *
 * 큐잉: 여러 Dialog 요청이 동시에 발생하면 순차적으로 표시 (스태킹 금지)
 *
 * 사용:
 *   const { confirm } = useDialog();
 *   const confirmed = await confirm({ title, message, type });
 */
export const DialogProvider = ({ children }) => {
  const [current, setCurrent] = useState(null);
  const queueRef = useRef([]);
  const currentRef = useRef(null);

  // currentRef를 current 상태와 동기화 (cleanup에서 최신 값 참조용)
  useEffect(() => {
    currentRef.current = current;
  }, [current]);

  /**
   * 큐에서 다음 Dialog를 꺼내서 표시
   */
  const showNext = useCallback(() => {
    if (queueRef.current.length > 0) {
      const next = queueRef.current.shift();
      setCurrent(next);
    } else {
      setCurrent(null);
    }
  }, []);

  /**
   * Dialog 확인 처리
   * passwordRequired 시 password 문자열, 아닐 시 true를 resolve
   */
  const handleConfirm = useCallback(
    (password) => {
      if (current) {
        if (current.config.passwordRequired) {
          current.resolve(password);
        } else {
          current.resolve(true);
        }
      }
      showNext();
    },
    [current, showNext]
  );

  /**
   * Dialog 취소 처리
   * passwordRequired 시 null, 아닐 시 false를 resolve
   */
  const handleCancel = useCallback(() => {
    if (current) {
      if (current.config.passwordRequired) {
        current.resolve(null);
      } else {
        current.resolve(false);
      }
    }
    showNext();
  }, [current, showNext]);

  /**
   * Confirm Dialog 표시 요청
   * @param {Object} config - Dialog 설정
   * @param {string} config.title - 대화상자 제목
   * @param {string} config.message - 대화상자 메시지
   * @param {string} [config.confirmText='확인'] - 확인 버튼 텍스트
   * @param {string} [config.cancelText='취소'] - 취소 버튼 텍스트
   * @param {'info'|'warning'|'error'} [config.type='warning'] - 유형
   * @param {boolean} [config.passwordRequired=false] - 비밀번호 입력 필드 표시 여부
   * @returns {Promise<boolean|string|null>} 확인 → true/password, 취소 → false/null
   */
  const confirm = useCallback(
    (config = {}) => {
      return new Promise((resolve) => {
        const item = { config, resolve };

        if (currentRef.current === null && queueRef.current.length === 0) {
          // 현재 표시 중인 Dialog가 없으면 즉시 표시
          // currentRef를 동기적으로 업데이트하여 동시 호출 시 큐잉이 올바르게 동작하도록 함
          currentRef.current = item;
          setCurrent(item);
        } else {
          // 이미 Dialog가 표시 중이면 큐에 추가
          queueRef.current.push(item);
        }
      });
    },
    []
  );

  // 컴포넌트 언마운트 시 큐 비우고 현재 Dialog resolve(false/null)
  useEffect(() => {
    return () => {
      // 현재 Dialog resolve
      if (currentRef.current) {
        if (currentRef.current.config.passwordRequired) {
          currentRef.current.resolve(null);
        } else {
          currentRef.current.resolve(false);
        }
      }
      // 큐에 남은 Dialog 모두 resolve
      queueRef.current.forEach((item) => {
        if (item.config.passwordRequired) {
          item.resolve(null);
        } else {
          item.resolve(false);
        }
      });
      queueRef.current = [];
    };
  }, []);

  // Context value를 useMemo로 메모이제이션하여 불필요한 리렌더 방지
  const value = useMemo(() => ({ confirm }), [confirm]);

  return (
    <DialogContext.Provider value={value}>
      {children}
      {/* ConfirmDialog 렌더링 영역 — Provider 내부에 포함 */}
      {current && (
        <ConfirmDialog
          title={current.config.title || ''}
          message={current.config.message || ''}
          confirmText={current.config.confirmText}
          cancelText={current.config.cancelText}
          type={current.config.type}
          passwordRequired={current.config.passwordRequired}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
        />
      )}
    </DialogContext.Provider>
  );
};

/**
 * useDialog 커스텀 훅
 * DialogProvider 외부에서 호출 시 에러를 throw한다.
 * @returns {{ confirm: Function }}
 */
export const useDialog = () => {
  const context = useContext(DialogContext);
  if (!context) {
    throw new Error('useDialog must be used within DialogProvider');
  }
  return context;
};
